import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Grid, Box, Skeleton, Typography } from '@mui/material';
import * as fileApi from '../../lib/api/file';

const Img = styled('img')({
  margin: 'auto',
  display: 'block',
  maxWidth: '100%',
  maxHeight: '100%',
});

const ProfileInfo = ({ employee }) => {
  const [profileImg, setProfileImg] = useState('');

  useEffect(() => {    
    const loadProfileImage = async () => {
      try {        
        setProfileImg(await fileApi.profileImage(employee.profileImg));
      } catch (error) {
        console.error("이미지 호출 에러:", error);
      }
    };

    if (employee?.profileImg) {
      loadProfileImage();
    }

    return () => {
      if (profileImg) URL.revokeObjectURL(profileImg);
    };
  }, [employee?.profileImg]);

  return (
    <Box sx={{ p: 2 }}>
      {/* 메인 전체 레이아웃 (좌측 사진 / 우측 정보) */}
      <Grid container spacing={2}>
        
        {/* [좌측] 프로필 이미지 영역 */}
        {employee && (
          <Grid item xs={12} md={3} textAlign="center">
            <Box
              sx={{
                width: 200,
                height: 170,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                overflow: 'hidden',
                borderRadius: 1,
                border: employee.profileImg ? 'none' : '1px solid #e0e0e0'
              }}
            >
              {employee.profileImg ? (
                <Img
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  alt={employee.usrNm}
                  src={profileImg}
                />
              ) : (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation={false}
                />
              )}
            </Box>
          </Grid>
        )}

        {/* [우측] 정보 노출 영역 (ProfileForm의 4-4-2 레이아웃 싱크) */}
        <Grid item xs={12} md={employee ? 9 : 12}>
          <Grid container spacing={1.5} alignItems="center">
            
            {/* ----------------- 첫 번째 줄 (4개 영역: 각 폼의 xs={3} 공간 분할) ----------------- */}
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">사번</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.usrNo}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">이름</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.usrNm}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">부서</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.dptNm}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">직급</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.gradeNm}</Typography></Grid>
            </Grid>

            {/* ----------------- 두 번째 줄 (4개 영역: 각 폼의 xs={3} 공간 분할) ----------------- */}
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">성별</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.gender === "M" ? '남성' : '여성'}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">연락처</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.telNo}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">이메일</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.emlAddr}</Typography></Grid>
            </Grid>
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">입사일</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.entryDt}</Typography></Grid>
            </Grid>

            {/* ----------------- 세 번째 줄 (1:3 비율 분할 -> xs={3}, xs={9}) ----------------- */}
            <Grid item xs={3} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">우편번호</Typography></Grid>
              <Grid item xs={8}><Typography variant="body2">{employee?.postAddrNm}</Typography></Grid>
            </Grid>
            <Grid item xs={9} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={1.3}><Typography variant="subtitle2" color="text.secondary">주소</Typography></Grid>
              <Grid item xs={10.7}><Typography variant="body2">{employee?.postAddrTxt}</Typography></Grid>
            </Grid>

            {/* ----------------- 네 번째 줄 (구글 캘린더 ID 추가 -> xs={12}) ----------------- */}
            <Grid item xs={12} container alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={1}><Typography variant="subtitle2" color="text.secondary">구글 캘린더 ID</Typography></Grid>
              <Grid item xs={11}><Typography variant="body2">{employee?.googleCalendarId || '-'}</Typography></Grid>
            </Grid>

            {/* ----------------- 다섯 번째 줄 (참고사항 -> xs={12}) ----------------- */}
            <Grid item xs={12} container alignItems="flex-start" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Grid item xs={1} sx={{ pt: 0.5 }}><Typography variant="subtitle2" color="text.secondary">참고사항</Typography></Grid>
              <Grid item xs={11}>
                <Typography sx={{ whiteSpace: 'pre-line' }} variant="body2">
                  {employee?.infoDesc || '-'}
                </Typography>
              </Grid>
            </Grid>

          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileInfo;
